import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'use-intl';
import type { MatchAttendanceStatus } from '@shared/schemas';
import { useMatchAttendance, useSaveMatchAttendance } from '@/app/queries';
import { sheetName } from '@/lib/format';
import { Button } from '@/ui/Button';
import { Notice, Panel } from '@/ui/Panel';
import { Section } from '@/ui/Section';
import { SheetNumber } from '@/ui/Sheet';

/**
 * The order a coach taps through on match day, best case first.
 *
 * Blank is reachable at the end: a player who has not been marked yet is not the
 * same as a player marked present, and a sheet that cannot be un-marked makes a
 * mistake permanent.
 */
const CYCLE: (MatchAttendanceStatus | '')[] = ['present', 'late', 'excused', 'absent', 'injured', ''];

/**
 * Who turned up, beside who was called up.
 *
 * Two facts, deliberately kept apart. The squad panel above says who the coach
 * picked; this says who appeared. Folding them into one list — called-up,
 * excused, absent, not selected — would put the coach's decision and the
 * player's behaviour in the same column, and they are opposite kinds of thing:
 * being left out is something done to a player, not by him.
 *
 * So the called-up are asked the question, and everybody else is listed under
 * the line without one, because "did he turn up" is not a question you can ask
 * about a player who was never asked to come.
 */
export function MatchAttendance({
  eventId,
  openByDefault = true,
}: {
  eventId: string;
  /** Match day is when this is the thing a coach is doing. */
  openByDefault?: boolean;
}) {
  const t = useTranslations();
  const attendance = useMatchAttendance(eventId);
  const save = useSaveMatchAttendance(eventId);

  const [marks, setMarks] = useState<Record<string, MatchAttendanceStatus>>({});
  const [hydrated, setHydrated] = useState<string | null>(null);

  useEffect(() => {
    if (!attendance.data || hydrated === eventId) return;
    const next: Record<string, MatchAttendanceStatus> = {};
    for (const p of attendance.data.players) {
      if (p.status) next[p.playerId] = p.status as MatchAttendanceStatus;
    }
    setMarks(next);
    setHydrated(eventId);
  }, [attendance.data, eventId, hydrated]);

  const players = attendance.data?.players ?? [];
  const calledUp = players.filter((p) => p.calledUp);
  const others = players.filter((p) => !p.calledUp);

  const counts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const p of calledUp) {
      const status = marks[p.playerId];
      if (status) out[status] = (out[status] ?? 0) + 1;
    }
    return out;
  }, [calledUp, marks]);
  const unmarked = calledUp.length - Object.values(counts).reduce((a, b) => a + b, 0);

  const cycle = (playerId: string) =>
    setMarks((current) => {
      const at = CYCLE.indexOf(current[playerId] ?? '');
      const next = CYCLE[(at + 1) % CYCLE.length]!;
      const updated = { ...current };
      if (next === '') delete updated[playerId];
      else updated[playerId] = next;
      return updated;
    });

  const markAll = (status: MatchAttendanceStatus) =>
    setMarks((current) => {
      const updated = { ...current };
      for (const p of calledUp) updated[p.playerId] = status;
      return updated;
    });

  if (calledUp.length === 0 && others.length === 0) return null;

  return (
    <Panel
      title={t('matchDay.title')}
      count={calledUp.length ? t('matchDay.ofCalledUp', { count: calledUp.length }) : undefined}
      collapsible
      storeKey="match.attendance"
      defaultOpen={openByDefault}
    >
      <Section title={t('matchDay.calledUp')} hint={t('matchDay.hint')}>
        {calledUp.length === 0 ? (
          <p className="text-meta text-ink-muted">{t('matchDay.nobodyCalledUp')}</p>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap gap-2">
              <Button variant="quiet" onClick={() => markAll('present')}>
                {t('matchDay.allPresent')}
              </Button>
            </div>
            <ul className="divide-y divide-line">
              {calledUp.map((p) => {
                const status = marks[p.playerId];
                return (
                  <li key={p.playerId} className="flex items-center gap-3 py-2.5">
                    <SheetNumber>{p.shirtNumber ?? '–'}</SheetNumber>
                    <span className="min-w-0 flex-1 truncate font-medium">
                      {sheetName(p.firstName, p.lastName)}
                    </span>
                    <span className="text-meta text-ink-faint">
                      {t(`match.${p.role === 'starter' ? 'starter' : 'bench'}`)}
                    </span>
                    <button
                      type="button"
                      onClick={() => cycle(p.playerId)}
                      aria-label={`${sheetName(p.firstName, p.lastName)} — ${
                        status ? t(`matchDay.status_${status}`) : t('matchDay.unmarked')
                      }`}
                      className={`min-h-11 min-w-28 rounded-(--radius-control) border px-3 text-meta transition-colors ${
                        status
                          ? 'border-ink bg-ink text-paper'
                          : 'border-line bg-surface text-ink-faint hover:border-ink'
                      }`}
                    >
                      {status ? t(`matchDay.status_${status}`) : t('matchDay.unmarked')}
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button
            variant="primary"
            busy={save.isPending}
            onClick={() =>
              void save.mutateAsync({
                entries: Object.entries(marks).map(([playerId, status]) => ({ playerId, status })),
              })
            }
          >
            {t('matchDay.save')}
          </Button>
          <span className="text-meta text-ink-muted">
            {CYCLE.filter((s): s is MatchAttendanceStatus => s !== '')
              .filter((s) => counts[s])
              .map((s) => `${counts[s]} ${t(`matchDay.status_${s}`).toLocaleLowerCase()}`)
              .join(' · ')}
            {unmarked > 0 ? ` · ${t('matchDay.unmarkedCount', { count: unmarked })}` : ''}
          </span>
        </div>
        {save.isError ? (
          <div className="mt-3">
            <Notice tone="risk">{t('common.saveFailed')}</Notice>
          </div>
        ) : null}
      </Section>

      {others.length > 0 ? (
        <Section title={t('matchDay.notCalledUp')} hint={t('matchDay.notCalledUpHint')}>
          <p className="text-meta text-ink-muted">
            {others.map((p) => sheetName(p.firstName, p.lastName)).join(' · ')}
          </p>
        </Section>
      ) : null}
    </Panel>
  );
}
